/* External Deps */
import { jsPDF } from 'jspdf';

/* Internal Deps */

// Text-selectable PDF generator using jsPDF directly
export class TextSelectablePDFGenerator {
  static async generatePDF(certificateData: FullCertificateData): Promise<void> {
    try {
      const { extractedData, _id } = certificateData;
      
      // Create new PDF document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'letter'
      });

      // Set font
      doc.setFont('helvetica');
      
      // Page dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const contentWidth = pageWidth - (margin * 2);
      
      let yPosition = margin + 20;

      // Helper function to add bordered section
      const addBorderedSection = (title: string, content: string[], startY: number): number => {
        const sectionHeight = Math.max(60, content.length * 20 + 40);
        
        // Draw border
        doc.rect(margin, startY, contentWidth, sectionHeight);
        
        // Add title
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(title, margin + 10, startY + 15);
        
        // Add content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        let contentY = startY + 35;
        
        content.forEach(line => {
          if (contentY + 15 < startY + sectionHeight - 5) {
            doc.text(line, margin + 20, contentY);
            contentY += 15;
          }
        });
        
        return startY + sectionHeight;
      };

      // Helper function to wrap text
      const wrapText = (text: string, maxWidth: number): string[] => {
        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = '';
        
        words.forEach(word => {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const textWidth = doc.getTextWidth(testLine);
          
          if (textWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        });
        
        if (currentLine) {
          lines.push(currentLine);
        }
        
        return lines;
      };

      // HEADER
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      const titleLines = [
        'CUSMA / USMCA / T-MEC CERTIFICATION OF ORIGIN',
        '',
        'Canada – United States – Mexico Agreement',
        'United States – Mexico – Canada Agreement',
        'Tratado entre México, Estados Unidos y Canadá'
      ];
      
      titleLines.forEach((line, index) => {
        if (line) {
          const textWidth = doc.getTextWidth(line);
          const xPosition = (pageWidth - textWidth) / 2;
          doc.setFontSize(index === 0 ? 16 : 10);
          doc.setFont('helvetica', index === 0 ? 'bold' : 'normal');
          doc.text(line, xPosition, yPosition);
        }
        yPosition += index === 0 ? 20 : 12;
      });
      
      // Header border
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 20;

      // FIELD 1: CERTIFIER
      const certifierContent = [
        `${extractedData.certifier === 'Importer' ? '☑' : '☐'} IMPORTER    ${extractedData.certifier === 'Exporter' ? '☑' : '☐'} EXPORTER    ${extractedData.certifier === 'Producer' ? '☑' : '☐'} PRODUCER`
      ];
      yPosition = addBorderedSection('1. CERTIFIER', certifierContent, yPosition);

      // FIELD 2: CERTIFIER DETAILS
      const certDetails = extractedData.certifierDetails || {};
      const certifierDetails = [
        `YOUR NAME: ${certDetails.yourName || '_________________________________'}`,
        `TITLE: ${certDetails.title || '_____________________________________'}`,
        `COMPANY NAME: ${certDetails.companyName || '_________________________________'}`,
        `ADDRESS: ${certDetails.address || '___________________________________'}`,
        `COUNTRY & POSTAL/ZIP CODE: ${certDetails.countryAndPostalCode || '__________________'}`,
        `PHONE: ${certDetails.phone || '________________'} EMAIL: ${certDetails.email || '______________'}`
      ];
      yPosition = addBorderedSection('2. CERTIFIER DETAILS', certifierDetails, yPosition);

      // FIELD 3: EXPORTER DETAILS
      const exporterDetails = [
        `COMPANY NAME: ${extractedData.exporterDetails?.companyName || '________________________________'}`,
        `ADDRESS: ${extractedData.exporterDetails?.address || '_____________________________________'}`,
        `COUNTRY & POSTAL/ZIP CODE: ${extractedData.exporterDetails?.countryAndPostalCode || '____________________________'}`,
        `PHONE: ${extractedData.exporterDetails?.phone || '_______________'}`,
        `EMAIL: ${extractedData.exporterDetails?.email || '________________________'}`
      ];
      yPosition = addBorderedSection('3. EXPORTER DETAILS (If different than Certifier)', exporterDetails, yPosition);

      // FIELD 4: PRODUCER DETAILS
      const prodDetails = extractedData.producerDetails;
      let producerDetails: string[];
      
      if (prodDetails && (prodDetails.companyName || prodDetails.address || prodDetails.phone || prodDetails.email)) {
        producerDetails = [
          `COMPANY NAME: ${prodDetails.companyName || '________________________________'}`,
          `ADDRESS: ${prodDetails.address || '_____________________________________'}`,
          `COUNTRY & POSTAL/ZIP CODE: ${prodDetails.countryAndPostalCode || '____________________________'}`,
          `PHONE: ${prodDetails.phone || '_______________'}`,
          `EMAIL: ${prodDetails.email || '________________________'}`
        ];
      } else {
        producerDetails = [
          'COMPANY NAME: Same as exporter',
          'ADDRESS: Same as exporter',
          'COUNTRY & POSTAL/ZIP CODE: Same as exporter',
          'PHONE: Same as exporter',
          'EMAIL: Same as exporter'
        ];
      }
      yPosition = addBorderedSection('4. PRODUCER DETAILS', producerDetails, yPosition);

      // FIELD 5: IMPORTER DETAILS
      const importerDetails = [
        `COMPANY NAME: ${extractedData.importerDetails?.companyName || '________________________________'}`,
        `ADDRESS: ${extractedData.importerDetails?.address || '_____________________________________'}`,
        `COUNTRY & POSTAL/ZIP CODE: ${extractedData.importerDetails?.countryAndPostalCode || '____________________________'}`,
        `PHONE: ${extractedData.importerDetails?.phone || '_______________'} EMAIL: ${extractedData.importerDetails?.email || '______________'}`
      ];
      yPosition = addBorderedSection('5. IMPORTER DETAILS (If known)', importerDetails, yPosition);

      // Check if we need a new page
      if (yPosition > pageHeight - 200) {
        doc.addPage();
        yPosition = margin + 20;
      }

      // FIELD 6a, 6b & 7: GOODS TABLE
      const tableStartY = yPosition;
      // Filter to only include items where INCLUDED === true
      const includedItemsCount = extractedData.items?.filter(item => item.INCLUDED).length || 0;
      const tableHeight = Math.max(150, (includedItemsCount || 1) * 40 + 60);
      
      // Table border
      doc.rect(margin, tableStartY, contentWidth, tableHeight);
      
      // Table title
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('6a, 6b & 7. GOODS DESCRIPTION AND ORIGIN CRITERIA', margin + 10, tableStartY + 15);
      
      // Table headers
      const col1Width = contentWidth * 0.2;
      const col2Width = contentWidth * 0.6;
      const col3Width = contentWidth * 0.2;
      
      let tableY = tableStartY + 35;
      
      // Header row
      doc.rect(margin, tableY, col1Width, 30);
      doc.rect(margin + col1Width, tableY, col2Width, 30);
      doc.rect(margin + col1Width + col2Width, tableY, col3Width, 30);
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      
      const header1Lines = wrapText('6a. HS TARIFF CLASSIFICATION (6-digit level)', col1Width - 10);
      const header2Lines = wrapText('6b. DESCRIPTION OF THE GOOD AND INVOICE NUMBER (If known)', col2Width - 10);
      const header3Lines = wrapText('7. ORIGIN CRITERION AS SET OUT IN ANNEX 4.2', col3Width - 10);
      
      header1Lines.forEach((line, index) => {
        doc.text(line, margin + 5, tableY + 10 + (index * 8));
      });
      
      header2Lines.forEach((line, index) => {
        doc.text(line, margin + col1Width + 5, tableY + 10 + (index * 8));
      });
      
      header3Lines.forEach((line, index) => {
        doc.text(line, margin + col1Width + col2Width + 5, tableY + 10 + (index * 8));
      });
      
      tableY += 30;
      
      // Data rows
      doc.setFont('helvetica', 'normal');
      
      // Filter to only include items where INCLUDED === true
      const includedItems = extractedData.items?.filter(item => item.INCLUDED) || [];
      
      if (includedItems.length > 0) {
        includedItems.forEach((item) => {
          const rowHeight = 40;
          
          // Row borders
          doc.rect(margin, tableY, col1Width, rowHeight);
          doc.rect(margin + col1Width, tableY, col2Width, rowHeight);
          doc.rect(margin + col1Width + col2Width, tableY, col3Width, rowHeight);
          
          // Cell content
          doc.text(item.hts || '______', margin + 5, tableY + 15);
          
          const descriptionLines = [
            item.description || 'Item Description',
            `Part #: ${item.partNumber || '_____'}`,
            `Invoice: ${item.invoiceNumber || '_____'}`,
            `Quantity: ${item.ship || '_____'}`
          ];
          
          descriptionLines.forEach((line, index) => {
            doc.text(line, margin + col1Width + 5, tableY + 10 + (index * 8));
          });
          
          // Origin and Criterion on separate lines
          doc.setFontSize(8);
          doc.text(`Origin: ${item.origin || 'N/A'}`, margin + col1Width + col2Width + 5, tableY + 15);
          doc.text(`Criterion: ${item.originCriterion || 'N/A'}`, margin + col1Width + col2Width + 5, tableY + 25);
          doc.setFontSize(9);
          
          tableY += rowHeight;
        });
      } else {
        // Empty row
        const rowHeight = 40;
        doc.rect(margin, tableY, col1Width, rowHeight);
        doc.rect(margin + col1Width, tableY, col2Width, rowHeight);
        doc.rect(margin + col1Width + col2Width, tableY, col3Width, rowHeight);
        doc.text('No included items', margin + col1Width + 5, tableY + 20);
        tableY += rowHeight;
      }
      
      yPosition = tableY + 10;

      // Check if we need a new page for remaining sections
      if (yPosition > pageHeight - 250) {
        doc.addPage();
        yPosition = margin + 20;
      }

      // FIELD 8: PERIOD COVERAGE
      const periodContent = [
        'FROM: _______________    TO: _______________'
      ];
      yPosition = addBorderedSection('8. PERIOD COVERED BY CERTIFICATION (If applicable)', periodContent, yPosition);

      // FIELD 9: CERTIFICATION
      const certificationHeight = 150;
      doc.rect(margin, yPosition, contentWidth, certificationHeight);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('9. CERTIFICATION', margin + 10, yPosition + 15);
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      
      const certificationText = 'I CERTIFY THAT THE GOODS DESCRIBED IN THIS DOCUMENT QUALIFY AS ORIGINATING AND THE INFORMATION CONTAINED IN THIS DOCUMENT IS TRUE AND ACCURATE. I ASSUME RESPONSIBILITY FOR PROVING SUCH REPRESENTATIONS AND AGREE TO MAINTAIN AND PRESENT UPON REQUEST OR TO MAKE AVAILABLE DURING A VERIFICATION VISIT, DOCUMENTATION NECESSARY TO SUPPORT THIS CERTIFICATION.';
      
      const certificationLines = wrapText(certificationText, contentWidth - 40);
      let certTextY = yPosition + 35;
      
      certificationLines.forEach(line => {
        doc.text(line, margin + 20, certTextY);
        certTextY += 10;
      });
      
      certTextY += 10;
      doc.text(`THIS CERTIFICATION CONSISTS OF ${doc.getNumberOfPages()} PAGES, INCLUDING ALL ATTACHMENTS.`, margin + 20, certTextY);
      
      // Signature section
      certTextY += 30;
      doc.text('SIGNATURE: ________________________________    DATE: ________________', margin + 20, certTextY);
      certTextY += 20;
      doc.setFont('helvetica', 'italic');
      doc.text('(SIGNED BY THE CERTIFIER LISTED ABOVE)', margin + 20 + (contentWidth - 300) / 2, certTextY);

      // Save the PDF
      const filename = `CUSMA_Certificate_${_id || 'Document'}.pdf`;
      doc.save(filename);
      
    } catch (error) {
      console.error('Error generating text-selectable PDF:', error);
      throw error;
    }
  }
}